import React, { useEffect, useState } from "react";
import api from "../../constants/api";
import { useAuth } from "../ui/AuthContext.jsx";
import { showError, showInfo } from "../../utils/toast.js";
import { isPowerUser } from "../../constants/roles.js";

/**
 * SharePoint Status + Öffnen — effektiv wenn Runtime ready UND sharepoint_storage.
 */
export default function SharePointEntityLink({
  entityType,
  entityId,
  projektTyp = "",
  className = "",
}) {
  const { sharepointReady, hasFeature } = useAuth();
  if (!sharepointReady || !hasFeature("sharepoint_storage")) return null;
  return (
    <SharePointEntityLinkInner
      entityType={entityType}
      entityId={entityId}
      projektTyp={projektTyp}
      className={className}
    />
  );
}

function SharePointEntityLinkInner({ entityType, entityId, projektTyp, className }) {
  const { user } = useAuth();
  const canOpenWebUrl = isPowerUser(user);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!entityType || !entityId) {
        setStatus("missing");
        return;
      }
      try {
        const q = new URLSearchParams({
          entityType: String(entityType),
          entityId: String(entityId),
        });
        if (projektTyp) q.set("projektTyp", projektTyp);
        const { data } = await api.get(`/sharepoint/entity-status?${q}`, { silent: true });
        if (cancelled) return;
        setStatus(data?.status || "missing");
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e?.message || "Fehler");
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, projektTyp]);

  async function openInSharePoint() {
    setBusy(true);
    try {
      const q = new URLSearchParams({
        entityType: String(entityType),
        entityId: String(entityId),
      });
      if (projektTyp) q.set("projektTyp", projektTyp);
      const { data } = await api.get(`/sharepoint/entity-link?${q}`, { silent: true });
      if (data?.web_url) {
        window.open(data.web_url, "_blank", "noopener,noreferrer");
        showInfo("SharePoint wird geöffnet …");
      } else {
        showError("Kein SharePoint-Link vorhanden.");
      }
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || "SharePoint konnte nicht geöffnet werden.");
    } finally {
      setBusy(false);
    }
  }

  const label =
    status === "linked"
      ? "SharePoint verknüpft"
      : status === "disabled"
        ? "SharePoint aus"
        : status === "error"
          ? "SharePoint Fehler"
          : status === "loading"
            ? "SharePoint…"
            : "SharePoint fehlt";

  const color =
    status === "linked"
      ? "bg-emerald-100 text-emerald-800"
      : status === "error"
        ? "bg-red-100 text-red-800"
        : "bg-slate-100 text-slate-700";

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}
        title={error || label}
      >
        {label}
      </span>
      {status === "linked" && canOpenWebUrl ? (
        <button
          type="button"
          onClick={openInSharePoint}
          disabled={busy}
          className="text-xs text-sky-700 underline hover:text-sky-900 disabled:opacity-50"
        >
          In SharePoint öffnen
        </button>
      ) : null}
    </div>
  );
}
